/* This file exists to consolidate all the places we interact with the OData server and other servers.
Eventually we want to implement authentication, which will happen here. 
*/

export class Failure {
    private className = "Failure"; // For debugging.

    // A very short message to flash to the user.
    public message: string = "Error Occurred";

    // More details if the user wants to have a good read.
    public details: string = "";

    public url?: string;
    public proxiedUrl?: string;
    public httpCode?: number;

    constructor(message: string, details: string) {
        this.message = message;
        this.details = details;
    }

    public toString() {
        return `${this.message} ${this.details} url:${this.url} `;
    }
}

export function failString(message: string) {
    return new Failure(message, "");
}

export function failError(error: Error) {
    return new Failure(error.message, String(error.stack));
}

// TODO: authentication, CORS. Tokens can be stored in the Fetcher. It's a class because it will one day be stateful.

export class Fetcher {
    private className = "Fetcher";
    private proxy : string|null = null;

    /** Use our custom OData proxy. */
    public UsingProxy(proxyPath: string) {
        this.proxy = proxyPath;
    }

    private proxied(url:URL|string) {
        if (null===this.proxy) {
            return url.toString();
        } else {
            return `${this.proxy}?url=${encodeURIComponent(url.toString())}`;
        }
    }

    /* Retrieve JSON from the given URL. */
    fetchJSON(ultimateUrl: URL): Promise<any> {
        let url : string = this.proxied(ultimateUrl);

        return new Promise<Document>((resolve, reject) => {
            return fetch(url)
                .then(response => {
                    if (response.ok) {
                        response.json().then(
                            json => {
                                resolve(json);
                            }, parseError => {
                                let f = new Failure(parseError.message, "while parsing json");
                                f.url = ultimateUrl.toString();
                                f.proxiedUrl = url;
                                reject(f);
                            });
                    } else {
                        return response.text().then(text => {
                            console.error("Bad fetch.");
                            let er = new Failure(`${response.status} ${response.statusText}`, text);
                            er.url = ultimateUrl.toString();
                            er.proxiedUrl = url;
                            return reject(er);
                        });
                    }
                }, (error) => {
                    let err: Error = error as Error;
                    let failure = new Failure(err.message, `Could not establish a network connection to ${url}. The URL might be incorrect, the server might be uncontactable or CORS might be misconfigured.`);
                    failure.url = ultimateUrl.toString();
                    failure.proxiedUrl = url;
                    reject(failure);
                });
        });
    }

    fetchJSONFromPath(ultimateUrl: string): Promise<any> {
        let url : string = this.proxied(ultimateUrl);
        return new Promise<Document>((resolve, reject) => {
            return fetch(url)
                .then(response => {
                    if (response.ok) {
                        response.json().then((json) => {
                            resolve(json);
                        }, parseError => {
                            let f = new Failure(parseError.message, "while parsing json");
                            f.url = url;
                            reject(f);
                        });
                    } else {
                        response.text().then(text => {
                            let er = new Failure(`${response.status} ${response.statusText}`, text);
                            er.url = url;
                            er.httpCode = response.status;
                            reject(er);
                        });
                    }
                }, (error) => {
                    let err: Error = error as Error;
                    let failure = new Failure(err.message, `Could not establish a network connection to ${url}. The URL might be incorrect, the server might be uncontactable or CORS might be misconfigured.`);
                    failure.url = url;
                    reject(failure);
                });
        });
    }

    fetchXML(ultimateUrl: URL): Promise<Document> {
        let url : string = this.proxied(ultimateUrl);

        return new Promise<Document>((resolve, reject) => {
            fetch(url)
                .then(response => {
                    if (response.ok) {
                        return response.text().then(text => {
                            let parser: DOMParser = new DOMParser();
                            let doc: Document = parser.parseFromString(text, "text/xml");
                            resolve(doc);
                        });
                    } else {
                        response.text().then(text => {
                            let message = `${response.status} ${response.statusText}`;
                            let failure: Failure = new Failure(message, text);
                            failure.url = url;
                            failure.httpCode = response.status;
                            reject(failure);
                        });
                    }
                }, (error) => {
                    let err: Error = error as Error;
                    let failure = new Failure(err.message, `Could not establish a network connection to ${url}. The URL might be incorrect, the server might be uncontactable or CORS might be misconfigured.`);
                    failure.url = url;
                    reject(failure);
                });
        });
    }
}